import { c as _c } from "react/compiler-runtime";
import { Form, Formik, useField } from 'formik';
import { useContext, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router';
import styled, { css, useTheme } from 'styled-components';
import { CONTAINER_WIDTHS } from '@hookdeck/theme';
import type { APITeam, ProjectTypeInput } from '../../../../../../../typings/Team.interface';
import { Promotion, Referrer, TrackingType } from '../../../../typings/Tracking.interface';
import { isInternalRedirectPath } from '../../../../utils';
import Alert from '../../../common/base/Alert';
import Badge from '../../../common/base/Badge';
import Button from '../../../common/base/Button';
import { StyledCard } from '../../../common/base/Card';
import Container from '../../../common/base/Container';
import Icon from '../../../common/base/Icon';
import Text from '../../../common/base/Text';
import { StyledRadioCard } from '../../../common/Form/Fields/RadioCardsInput';
import TextAreaInput from '../../../common/Form/Fields/TextAreaInput';
import TextInput from '../../../common/Form/Fields/TextInput';
import { Div } from '../../../common/helpers/StyledUtils';
import { useToasts } from '../../../common/Toast';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { UserContext } from '../../../contexts/UserContext';
import useLocalStorage from '../../../hooks/useLocalStorage';
import FullscreenLayout from '../../../layouts/FullscreenLayout';
import { DashboardContext } from '../DashboardContext';
import { PRODUCT_SLUGS } from '../../../../../../../domain/products';
const StepBage = styled(Badge)`
  font-size: 10px;
  min-width: 20px;
  min-height: 20px;
  line-height: 20px;
  font-weight: 600;
  font-variant: unset;
`;
const StepRow = styled(Div)(({
  theme
}) => css`
    display: flex;
    gap: ${theme.spacing(6)};
  `);
const StepProgress = styled.div<{
  is_last?: boolean;
}>(({
  theme,
  is_last
}) => css`
    position: relative;
    flex: 0 0 20px;
    width: 20px;
    display: flex;
    justify-content: center;
    align-items: flex-start;

    ${!is_last && css`
      &::after {
        content: '';
        position: absolute;
        top: 20px;
        bottom: 0;
        left: 50%;
        width: 1px;
        transform: translateX(-50%);
        background-color: ${theme.colors.surface.base.variant_surface};
      }
    `}

    > * {
      position: relative;
      z-index: 1;
    }
  `);
const DEFAULT_EVENT_GATEWAY_REDIRECT_TO = '/create-first-connection';
const DEFAULT_EVENT_GATEWAY_PROJECT_NAME = 'Event Gateway Starter';
const DEFAULT_OUTPOST_PROJECT_NAME = 'Outpost Starter';
const ONBOARDING_USE_CASE_STORAGE_KEY = 'pref:onboarding:use_case';
type OnboardingProjectType = ProjectTypeInput;
type OnboardingUseCase = 'receive_webhooks' | 'send_webhooks';
const getUseCaseProjectType = (use_case: string): OnboardingProjectType => use_case === 'send_webhooks' ? 'outpost' : 'event_gateway';
const getUseCaseByProjectType = (type: OnboardingProjectType): OnboardingUseCase => type === 'outpost' ? 'send_webhooks' : 'receive_webhooks';
const getTeamType = (team?: APITeam | null): OnboardingProjectType => team?.type === 'outpost' ? 'outpost' : 'event_gateway';
const getDefaultProjectNameByType = (type: OnboardingProjectType) => type === 'outpost' ? DEFAULT_OUTPOST_PROJECT_NAME : DEFAULT_EVENT_GATEWAY_PROJECT_NAME;
const findTeamByTypeAndName = (teams: APITeam[] | undefined | null, organization_id: string, type: OnboardingProjectType, name: string) => {
  return teams?.find(team => team.organization_id === organization_id && getTeamType(team) === type && team.name === name);
};
const getOnboardingSubmitLabel = ({
  organization,
  team,
  teams,
  use_case
}: {
  organization?: {
    id: string;
  } | null;
  team?: APITeam | null;
  teams?: APITeam[] | null;
  use_case: string;
}) => {
  if (!organization) {
    return 'Create Project';
  }
  if (!team || !teams) {
    return 'Continue';
  }
  const selected_type = getUseCaseProjectType(use_case);
  const project_name = getDefaultProjectNameByType(selected_type);
  const existing_starter_team = findTeamByTypeAndName(teams, organization.id, selected_type, project_name);
  return existing_starter_team ? 'Continue' : 'Create Project';
};
const USE_CASE_OPTIONS = [{
  label: 'Event Gateway',
  value: 'receive_webhooks',
  redirect_to: DEFAULT_EVENT_GATEWAY_REDIRECT_TO,
  badge: 'Most common',
  description: 'Handle events from external providers before they hit your application.',
  image: 'usecase-receive',
  bullets: [{
    icon: 'requests',
    text: 'Receive webhooks from third-party services'
  }, {
    icon: 'transformation',
    text: 'Queue, route, filter & transform events'
  }, {
    icon: 'terminal',
    text: 'Test and debug on your localhost'
  }]
}, {
  label: 'Outpost',
  value: 'send_webhooks',
  redirect_to: '/get-started',
  badge: undefined,
  description: 'Deliver outbound webhooks and events from your API, SaaS, or PaaS.',
  image: 'usecase-send',
  bullets: [{
    icon: 'events',
    text: 'Send webhooks to your users'
  }, {
    icon: 'group',
    text: 'Multi-tenant subscriptions and topics'
  }, {
    icon: 'webhook',
    text: 'Deliver to webhooks, queues, streams'
  }]
}] as const;
interface UseCaseCardsInputProps {
  name: string;
  options: typeof USE_CASE_OPTIONS;
}
const UseCaseCardsInput: React.FC<UseCaseCardsInputProps> = t0 => {
  const $ = _c(16);
  const {
    name,
    options
  } = t0;
  const [field,, t1] = useField(name);
  const {
    setValue,
    setTouched
  } = t1;
  const theme = useTheme();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      gap: 6
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== field || $[2] !== name || $[3] !== options || $[4] !== setTouched || $[5] !== setValue || $[6] !== theme) {
    let t4;
    if ($[8] !== field || $[9] !== name || $[10] !== setTouched || $[11] !== setValue || $[12] !== theme) {
      t4 = option => {
        const is_checked = field.value === option.value;
        return <StyledRadioCard as="label" data-testid={`onboarding-use-case-${option.value}`} key={option.value} checked={is_checked} style={{
          backgroundColor: theme.colors.surface.base.surface
        }} flex={{
          direction: "column"
        }} w={{
          px: CONTAINER_WIDTHS.small
        }}><Div p={{
            x: 4,
            t: 4,
            b: 6
          }}><Div flex={{
              align: "flex-start",
              gap: 4
            }}><input style={{
                marginTop: "1px"
              }} type="radio" name={name} value={option.value} checked={is_checked} onChange={() => {
                setValue(option.value);
                setTouched(true);
              }} /><Div style={{
                flex: 1,
                minWidth: 0
              }}><Div flex={{
                  align: "center",
                  gap: 3
                }}><Text semi={true}>{option.label}</Text>{option.badge && <Badge neutral={true}>{option.badge}</Badge>}</Div><Text muted={true} size="m" m={{
                  t: 1
                }}>{option.description}</Text><Div m={{
                  t: 3
                }} flex={{
                  direction: "column",
                  gap: 2
                }}>{option.bullets.map(_temp)}</Div></Div></Div></Div><Div flex={true} p={{
            x: 2,
            b: 2
          }}><StyledCard overflow_hidden={true} flex={{
              align: "center",
              justify: "center"
            }} style={{
              backgroundColor: theme.colors.surface.base.background,
              minHeight: "176px",
              width: "100%"
            }}><img src={`/images/${option.image}-${theme.mode}.png`} alt={option.label} style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain"
              }} /></StyledCard></Div></StyledRadioCard>;
      };
      $[8] = field;
      $[9] = name;
      $[10] = setTouched;
      $[11] = setValue;
      $[12] = theme;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    t3 = options.map(t4);
    $[1] = field;
    $[2] = name;
    $[3] = options;
    $[4] = setTouched;
    $[5] = setValue;
    $[6] = theme;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[14] !== t3) {
    t4 = <Div flex={t2} role="radiogroup">{t3}</Div>;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};
const CreateOrganization: React.FC = () => {
  const $ = _c(49);
  const {
    HookdeckAPI,
    setAPITeamId
  } = useContext(GlobalContext);
  const {
    user
  } = useContext(UserContext);
  const {
    organization,
    team,
    teams,
    mutateOrganization,
    mutateOrganizations,
    mutateTeam,
    mutateTeams
  } = useContext(DashboardContext);
  const {
    addToast
  } = useToasts();
  const submitRef = useRef(false);
  const history = useHistory();
  const location = useLocation();
  const [promotion] = useLocalStorage(TrackingType.promotion, null);
  const [referrer] = useLocalStorage(TrackingType.referrer, null);
  const [stored_use_case, setStoredUseCase] = useLocalStorage(ONBOARDING_USE_CASE_STORAGE_KEY, "");
  let t0;
  if ($[0] !== location.search) {
    const search_params = new URLSearchParams(location.search);
    t0 = search_params.get("redirect");
    $[0] = location.search;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const redirect = t0;
  const decoded_redirect = redirect ? decodeURIComponent(redirect) : null;
  let t1;
  if ($[2] !== decoded_redirect) {
    t1 = decoded_redirect?.includes("/cli-auth/");
    $[2] = decoded_redirect;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const is_cli_auth_flow = t1;
  const has_stored_use_case = stored_use_case === "receive_webhooks" || stored_use_case === "send_webhooks";
  let t2;
  if ($[4] !== has_stored_use_case || $[5] !== organization || $[6] !== stored_use_case || $[7] !== team || $[8] !== user?.product_source) {
    t2 = has_stored_use_case ? stored_use_case as OnboardingUseCase : organization && team ? getUseCaseByProjectType(getTeamType(team)) : user?.product_source === PRODUCT_SLUGS.outpost ? "send_webhooks" : "receive_webhooks";
    $[4] = has_stored_use_case;
    $[5] = organization;
    $[6] = stored_use_case;
    $[7] = team;
    $[8] = user?.product_source;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const initial_use_case = t2;
  let t3;
  if ($[10] !== HookdeckAPI.track || $[11] !== promotion || $[12] !== referrer) {
    t3 = () => {
      if (referrer || promotion) {
        const trackingData = {
          promotion: promotion?.slug,
          referrer: referrer?.slug
        };
        HookdeckAPI.track.event("Signup tracked", trackingData).catch(_temp2);
      }
    };
    $[10] = HookdeckAPI.track;
    $[11] = promotion;
    $[12] = referrer;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  useEffect(t3, t4);
  let t5;
  if ($[15] !== HookdeckAPI.track || $[16] !== decoded_redirect || $[17] !== history) {
    t5 = redirect_to => {
      HookdeckAPI.track.event("Completed Onboarding");
      if (decoded_redirect && isInternalRedirectPath(decoded_redirect)) {
        return history.push(decoded_redirect);
      }
      return history.push(redirect_to);
    };
    $[15] = HookdeckAPI.track;
    $[16] = decoded_redirect;
    $[17] = history;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const completeOnboarding = t5;
  let t6;
  if ($[19] !== HookdeckAPI.organizations || $[20] !== HookdeckAPI.teams || $[21] !== completeOnboarding || $[22] !== mutateOrganization || $[23] !== mutateOrganizations || $[24] !== mutateTeam || $[25] !== mutateTeams || $[26] !== organization || $[27] !== setAPITeamId || $[28] !== team || $[29] !== teams) {
    t6 = async (name, use_case, redirect_to_0) => {
      const selected_type = getUseCaseProjectType(use_case);
      const project_name = getDefaultProjectNameByType(selected_type);
      if (!organization) {
        const {
          default_team,
          organization: created_organization
        } = await HookdeckAPI.organizations.create({
          name,
          type: selected_type,
          project_name
        });
        mutateOrganization(created_organization);
        await mutateOrganizations();
        mutateTeam(default_team);
        await mutateTeams();
        setAPITeamId(default_team.id);
        return completeOnboarding(redirect_to_0);
      }
      if (!team || !teams) {
        throw new Error("Project list is still loading.");
      }
      const updated_organization = await HookdeckAPI.organizations.update({
        name
      });
      mutateOrganization(updated_organization);
      await mutateOrganizations();
      let target_team = findTeamByTypeAndName(teams, organization.id, selected_type, project_name);
      if (!target_team) {
        target_team = await HookdeckAPI.teams.create({
          organization_id: organization.id,
          name: project_name,
          private: false,
          type: selected_type
        });
        await mutateTeams();
      }
      mutateTeam(target_team);
      setAPITeamId(target_team.id);
      return completeOnboarding(redirect_to_0);
    };
    $[19] = HookdeckAPI.organizations;
    $[20] = HookdeckAPI.teams;
    $[21] = completeOnboarding;
    $[22] = mutateOrganization;
    $[23] = mutateOrganizations;
    $[24] = mutateTeam;
    $[25] = mutateTeams;
    $[26] = organization;
    $[27] = setAPITeamId;
    $[28] = team;
    $[29] = teams;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  const submitOnboarding = t6;
  const t7 = organization?.name || "";
  let t8;
  if ($[31] !== initial_use_case || $[32] !== t7) {
    t8 = {
      name: t7,
      discovery_method: "",
      use_case: initial_use_case
    };
    $[31] = initial_use_case;
    $[32] = t7;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  let t9;
  if ($[34] !== HookdeckAPI.track || $[35] !== addToast || $[36] !== setStoredUseCase || $[37] !== submitOnboarding || $[38] !== user) {
    t9 = async values => {
      if (submitRef.current === true) {
        return;
      }
      submitRef.current = true;
      const {
        name: name_0,
        discovery_method,
        use_case: use_case_0
      } = values;
      setStoredUseCase(use_case_0);
      if (user) {
        const use_case_position = USE_CASE_OPTIONS.findIndex(t10 => {
          const {
            value
          } = t10;
          return value === use_case_0;
        });
        HookdeckAPI.track.event("Submitted Onboarding", {
          discovery_method,
          use_case: use_case_0,
          use_case_position,
          $set_once: {
            discovery_method,
            use_case: use_case_0,
            use_case_position
          }
        }).catch(_temp4);
      }
      const redirect_to_1 = USE_CASE_OPTIONS.find(t11 => {
        const {
          value: value_0
        } = t11;
        return value_0 === use_case_0;
      })?.redirect_to || DEFAULT_EVENT_GATEWAY_REDIRECT_TO;
      try {
        await submitOnboarding(name_0, use_case_0, redirect_to_1);
        submitRef.current = false;
      } catch {
        submitRef.current = false;
        addToast("error", "Failed to create your organization, try again!");
      }
    };
    $[34] = HookdeckAPI.track;
    $[35] = addToast;
    $[36] = setStoredUseCase;
    $[37] = submitOnboarding;
    $[38] = user;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  let t10;
  if ($[40] !== is_cli_auth_flow || $[41] !== organization || $[42] !== team || $[43] !== teams) {
    t10 = props => {
      const submit_label = getOnboardingSubmitLabel({
        organization,
        team,
        teams,
        use_case: props.values.use_case
      });
      return <Form><Div flex={{
          direction: "column",
          align: "center"
        }}><Div w={{
            px: CONTAINER_WIDTHS.large
          }} style={{
            textAlign: "left"
          }}><Div style={{
              textAlign: "center"
            }} m={{
              b: 14
            }}><Text bold={true} size="3xl" m={{
                b: 1
              }}>Welcome to Hookdeck</Text><Text muted={true} size="l">Receive, process, and deliver messages across your event-driven applications.</Text></Div>{is_cli_auth_flow && <Alert info={true} inline={true} m={{
              b: 8
            }}>Once the organization is created, your CLI will be authenticated.</Alert>}<StepRow><StepProgress><StepBage primary={true} subtle={true}>1</StepBage></StepProgress><Div p={{
                b: 14
              }} style={{
                flex: 1,
                minWidth: 0
              }}><Text semi={true} size="l" m={{
                  b: 1
                }}>Name your organization</Text><Text muted={true} m={{
                  b: 4
                }}>Organizations house projects and any team members you work with.</Text><TextInput name="name" m={{
                  b: 0
                }} required={true} placeholder="Organization name" /></Div></StepRow><StepRow><StepProgress><StepBage primary={true} subtle={true}>2</StepBage></StepProgress><Div p={{
                b: 14
              }} style={{
                flex: 1,
                minWidth: 0
              }}><Text semi={true} size="l" m={{
                  b: 1
                }}>Select the product you'd like to use</Text><Text muted={true} m={{
                  b: 6
                }}>Choose the Hookdeck offering you want to get started with.</Text><UseCaseCardsInput name="use_case" options={USE_CASE_OPTIONS} /></Div></StepRow><StepRow><StepProgress is_last={true}><StepBage neutral={true} subtle={true}><Icon icon="help" small={true} muted={true} /></StepBage></StepProgress><Div style={{
                flex: 1,
                minWidth: 0
              }}><Text semi={true} size="l" m={{
                  b: 4
                }}>How did you hear about us?</Text><TextAreaInput m={{
                  b: 0
                }} name="discovery_method" placeholder="Claude, ChatGPT, Gemini, Google search, X, etc." /></Div></StepRow><Div flex={{
              justify: "flex-end"
            }} m={{
              t: 14
            }}><Button large={true} disabled={props.isSubmitting || !props.isValid} primary={true} submit={true} icon={props.isSubmitting ? "loading" : undefined}>{!props.isSubmitting && <>{submit_label}</>}</Button></Div></Div></Div></Form>;
    };
    $[40] = is_cli_auth_flow;
    $[41] = organization;
    $[42] = team;
    $[43] = teams;
    $[44] = t10;
  } else {
    t10 = $[44];
  }
  let t11;
  if ($[45] !== t10 || $[46] !== t8 || $[47] !== t9) {
    t11 = <FullscreenLayout center={true} no_decorations={true}><Container xxlarge={true}><Formik initialValues={t8} validateOnMount={true} validate={_temp3} onSubmit={t9}>{t10}</Formik></Container></FullscreenLayout>;
    $[45] = t10;
    $[46] = t8;
    $[47] = t9;
    $[48] = t11;
  } else {
    t11 = $[48];
  }
  return t11;
};
export default CreateOrganization;
function _temp(t0, index) {
  const {
    icon,
    text
  } = t0;
  return <Text key={index} dark={true} size="m" flex={{
    align: "center",
    gap: 2
  }}><Icon icon={icon} primary={true} />{text}</Text>;
}
function _temp2() {
  return null;
}
function _temp3(v) {
  return !v.name || v.name.length === 0 ? {
    name: "Required "
  } : {};
}
function _temp4() {
  return null;
}